import { useState, useEffect } from 'react';
import { BarChart3, Package, MapPin, TrendingUp, AlertCircle, Sparkles, DollarSign, Activity } from 'lucide-react';

const API_URL = '';

function PerformanceSection({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API_URL}/api/admin/analytics/performance`, {
      headers: { Cookie: `access_token=${token}` },
      credentials: 'include',
    })
      .then(r => r.json())
      .then(d => { setData(d.performance); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <p className="text-sm text-muted-foreground py-4 text-center">Loading performance data...</p>;
  if (!data) return null;

  const { summary, by_category, by_product, by_region } = data;

  return (
    <div className="space-y-6" data-testid="performance-section">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="p-4 bg-white rounded-lg border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Impressions</p>
          <p className="text-2xl font-medium mt-1" data-testid="total-impressions">{summary.total_impressions}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Clicks</p>
          <p className="text-2xl font-medium mt-1">{summary.total_clicks}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Overall CTR</p>
          <p className="text-2xl font-medium mt-1">{summary.overall_ctr}%</p>
        </div>
      </div>

      {by_category.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5">
          <h4 className="text-sm font-semibold mb-4">CTR by Category</h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 pr-4">Category</th>
                <th className="pb-2 pr-4 text-right">Impressions</th>
                <th className="pb-2 pr-4 text-right">Clicks</th>
                <th className="pb-2 text-right">CTR</th>
              </tr>
            </thead>
            <tbody>
              {by_category.map(c => (
                <tr key={c.category} className="border-b border-border/50">
                  <td className="py-2 pr-4 font-medium capitalize">{c.category.replace(/_/g, ' ')}</td>
                  <td className="py-2 pr-4 text-right">{c.impressions}</td>
                  <td className="py-2 pr-4 text-right">{c.clicks}</td>
                  <td className="py-2 text-right">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      c.ctr >= 5 ? 'bg-green-100 text-green-700' : c.ctr > 0 ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'
                    }`}>{c.ctr}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {by_product.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5">
          <h4 className="text-sm font-semibold mb-4">CTR by Product</h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 pr-4">Product</th>
                <th className="pb-2 pr-4">Category</th>
                <th className="pb-2 pr-4 text-right">Impr.</th>
                <th className="pb-2 pr-4 text-right">Clicks</th>
                <th className="pb-2 text-right">CTR</th>
              </tr>
            </thead>
            <tbody>
              {by_product.slice(0, 15).map((p, i) => (
                <tr key={`${p.product_id}-${i}`} className="border-b border-border/50">
                  <td className="py-2 pr-4 font-medium">{p.product_name}</td>
                  <td className="py-2 pr-4 capitalize">{p.category?.replace(/_/g, ' ')}</td>
                  <td className="py-2 pr-4 text-right">{p.impressions}</td>
                  <td className="py-2 pr-4 text-right">{p.clicks}</td>
                  <td className="py-2 text-right">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      p.ctr >= 5 ? 'bg-green-100 text-green-700' : p.ctr > 0 ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'
                    }`}>{p.ctr}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {by_region.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5">
          <h4 className="text-sm font-semibold mb-4">CTR by Region</h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 pr-4">Region</th>
                <th className="pb-2 pr-4 text-right">Impressions</th>
                <th className="pb-2 pr-4 text-right">Clicks</th>
                <th className="pb-2 text-right">CTR</th>
              </tr>
            </thead>
            <tbody>
              {by_region.map(r => (
                <tr key={r.region} className="border-b border-border/50">
                  <td className="py-2 pr-4 font-medium">{r.region}</td>
                  <td className="py-2 pr-4 text-right">{r.impressions}</td>
                  <td className="py-2 pr-4 text-right">{r.clicks}</td>
                  <td className="py-2 text-right">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      r.ctr >= 5 ? 'bg-green-100 text-green-700' : r.ctr > 0 ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'
                    }`}>{r.ctr}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MonetizationSection({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API_URL}/api/admin/analytics/monetization`, {
      headers: { Cookie: `access_token=${token}` },
      credentials: 'include',
    })
      .then(r => r.json())
      .then(d => { setData(d.monetization); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <p className="text-sm text-muted-foreground py-4 text-center">Loading monetization data...</p>;
  if (!data) return null;

  const { summary, category_coverage, high_click_no_link, pending_review } = data;

  return (
    <div className="space-y-6" data-testid="monetization-section">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-white rounded-lg border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Linked Products</p>
          <p className="text-2xl font-medium mt-1 text-green-700">{summary.linked}</p>
          <p className="text-[10px] text-muted-foreground">{Math.round(summary.linked / Math.max(summary.total_products, 1) * 100)}% coverage</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Suggestions Pending</p>
          <p className="text-2xl font-medium mt-1 text-purple-700">{summary.suggestions_pending}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Need Links</p>
          <p className="text-2xl font-medium mt-1 text-amber-700">{summary.no_link}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Clicks</p>
          <p className="text-2xl font-medium mt-1">{summary.total_clicks}</p>
        </div>
      </div>

      {/* Category coverage */}
      <div className="bg-white rounded-lg border border-border p-5">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-4 h-4 text-primary" />
          <h4 className="text-sm font-semibold">Category Link Coverage</h4>
          <span className="text-[10px] text-muted-foreground ml-1">Categories with weak link coverage = missed revenue</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 pr-4">Category</th>
                <th className="pb-2 pr-4 text-center">Products</th>
                <th className="pb-2 pr-4 text-center">Linked</th>
                <th className="pb-2 pr-4 text-center">Suggested</th>
                <th className="pb-2 pr-4 text-center">No Link</th>
                <th className="pb-2 pr-4 text-center">Clicks</th>
                <th className="pb-2 text-right">Coverage</th>
              </tr>
            </thead>
            <tbody>
              {category_coverage.map(c => (
                <tr key={c.category} className="border-b border-border/50">
                  <td className="py-2 pr-4 font-medium capitalize">{c.category.replace(/_/g, ' ')}</td>
                  <td className="py-2 pr-4 text-center">{c.total}</td>
                  <td className="py-2 pr-4 text-center text-green-700 font-medium">{c.linked}</td>
                  <td className="py-2 pr-4 text-center text-purple-700">{c.suggested}</td>
                  <td className="py-2 pr-4 text-center text-amber-700">{c.no_link}</td>
                  <td className="py-2 pr-4 text-center">{c.clicks}</td>
                  <td className="py-2 text-right">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      c.link_coverage_pct >= 50 ? 'bg-green-100 text-green-700' :
                      c.link_coverage_pct > 0 ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>{c.link_coverage_pct}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* High-click products without links */}
      {high_click_no_link.length > 0 && (
        <div className="bg-white rounded-lg border border-amber-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-amber-600" />
            <h4 className="text-sm font-semibold">Top Missed Monetization Opportunities</h4>
            <span className="text-[10px] text-muted-foreground ml-1">High-click products without affiliate links</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-4">Product</th>
                  <th className="pb-2 pr-4">Category</th>
                  <th className="pb-2 pr-4 text-center">Clicks</th>
                  <th className="pb-2 pr-4 text-center">Suggestion</th>
                  <th className="pb-2 text-right">Retailer</th>
                </tr>
              </thead>
              <tbody>
                {high_click_no_link.slice(0, 15).map((p, i) => (
                  <tr key={`${p.product_id}-${i}`} className="border-b border-border/50">
                    <td className="py-2 pr-4 font-medium">{p.product_name}</td>
                    <td className="py-2 pr-4 capitalize">{p.category?.replace(/_/g, ' ')}</td>
                    <td className="py-2 pr-4 text-center font-semibold">{p.clicks}</td>
                    <td className="py-2 pr-4 text-center">
                      {p.suggestion_status !== 'no_link' ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-100 text-purple-700">{p.suggestion_status.replace(/_/g, ' ')}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2 text-right text-muted-foreground">{p.suggested_retailer || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pending review suggestions */}
      {pending_review.length > 0 && (
        <div className="bg-white rounded-lg border border-purple-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <h4 className="text-sm font-semibold">High-Confidence Suggestions Awaiting Review</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-4">Product</th>
                  <th className="pb-2 pr-4">Category</th>
                  <th className="pb-2 pr-4">Retailer</th>
                  <th className="pb-2 pr-4 text-center">Confidence</th>
                  <th className="pb-2 pr-4 text-center">Relevance</th>
                  <th className="pb-2 pr-4 text-center">Clicks</th>
                  <th className="pb-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {pending_review.map((p, i) => (
                  <tr key={`${p.product_id}-${i}`} className="border-b border-border/50">
                    <td className="py-2 pr-4 font-medium">{p.product_name}</td>
                    <td className="py-2 pr-4 capitalize">{p.category?.replace(/_/g, ' ')}</td>
                    <td className="py-2 pr-4">{p.suggested_retailer || '—'}</td>
                    <td className="py-2 pr-4 text-center">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        p.suggestion_confidence === 'high' ? 'bg-green-100 text-green-700' :
                        p.suggestion_confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>{p.suggestion_confidence}</span>
                    </td>
                    <td className="py-2 pr-4 text-center capitalize">{(p.suggestion_relevance || '—').replace(/_/g, ' ')}</td>
                    <td className="py-2 pr-4 text-center font-medium">{p.clicks}</td>
                    <td className="py-2 text-center">
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-100 text-purple-700">{p.suggestion_status.replace(/_/g, ' ')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ClickAnalyticsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API_URL}/api/admin/analytics/clicks`, {
      headers: { Cookie: `access_token=${token}` },
      credentials: 'include',
    })
      .then(r => r.json())
      .then(d => { setData(d.analytics); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <p className="text-sm text-muted-foreground py-8 text-center">Loading analytics...</p>;
  if (!data) return <p className="text-sm text-muted-foreground py-8 text-center">Unable to load analytics.</p>;

  const maxCatClicks = Math.max(...(data.by_category?.map(c => c.clicks) || [1]), 1);
  const maxRegClicks = Math.max(...(data.by_region?.map(r => r.clicks) || [1]), 1);

  return (
    <div className="space-y-6" data-testid="click-analytics-tab">
      {/* Click summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-white rounded-lg border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Clicks</p>
          <p className="text-2xl font-medium mt-1" data-testid="total-clicks">{data.total_clicks}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Categories Active</p>
          <p className="text-2xl font-medium mt-1">{data.by_category?.length || 0}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Regions Active</p>
          <p className="text-2xl font-medium mt-1">{data.by_region?.length || 0}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Zero-Click Products</p>
          <p className="text-2xl font-medium mt-1">{data.zero_click_count} / {data.total_approved_products}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Clicks per category */}
        <div className="bg-white rounded-lg border border-border p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-primary" />
            <h4 className="text-sm font-semibold">Clicks by Category</h4>
          </div>
          {data.by_category?.length > 0 ? (
            <div className="space-y-2">
              {data.by_category.map(c => (
                <div key={c.category} className="flex items-center gap-3">
                  <span className="text-xs w-24 text-right capitalize truncate">{c.category.replace(/_/g, ' ')}</span>
                  <div className="flex-1 bg-muted rounded-full h-5 overflow-hidden">
                    <div
                      className="bg-primary/70 h-full rounded-full flex items-center justify-end pr-2 transition-all"
                      style={{ width: `${Math.max((c.clicks / maxCatClicks) * 100, 8)}%` }}
                    >
                      <span className="text-[10px] text-white font-medium">{c.clicks}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No click data yet</p>
          )}
        </div>

        {/* Clicks per region */}
        <div className="bg-white rounded-lg border border-border p-5">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-4 h-4 text-primary" />
            <h4 className="text-sm font-semibold">Clicks by Region</h4>
          </div>
          {data.by_region?.length > 0 ? (
            <div className="space-y-2">
              {data.by_region.map(r => (
                <div key={r.region} className="flex items-center gap-3">
                  <span className="text-xs w-28 text-right truncate">{r.region}</span>
                  <div className="flex-1 bg-muted rounded-full h-5 overflow-hidden">
                    <div
                      className="bg-green-600/70 h-full rounded-full flex items-center justify-end pr-2 transition-all"
                      style={{ width: `${Math.max((r.clicks / maxRegClicks) * 100, 8)}%` }}
                    >
                      <span className="text-[10px] text-white font-medium">{r.clicks}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No regional data yet</p>
          )}
        </div>
      </div>

      {/* Top products by clicks */}
      <div className="bg-white rounded-lg border border-border p-5">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-primary" />
          <h4 className="text-sm font-semibold">Top Products by Clicks</h4>
        </div>
        {data.by_product?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-4">Product</th>
                  <th className="pb-2 pr-4">Manufacturer</th>
                  <th className="pb-2 pr-4">Category</th>
                  <th className="pb-2 text-right">Clicks</th>
                </tr>
              </thead>
              <tbody>
                {data.by_product.map((p, i) => (
                  <tr key={`${p.product_id}-${i}`} className="border-b border-border/50">
                    <td className="py-2 pr-4 font-medium">{p.product_name}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{p.manufacturer}</td>
                    <td className="py-2 pr-4 capitalize">{p.category?.replace(/_/g, ' ')}</td>
                    <td className="py-2 text-right font-semibold">{p.clicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">No product click data yet</p>
        )}
      </div>

      {/* Performance: Impressions vs Clicks / CTR */}
      <div className="border-t border-border pt-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-primary" />
          <h3 className="text-base font-semibold" style={{ fontFamily: 'Outfit' }}>Performance (Impressions vs Clicks)</h3>
        </div>
        <PerformanceSection token={token} />
      </div>

      {/* Monetization Opportunities */}
      <div className="border-t border-border pt-6">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-primary" />
          <h3 className="text-base font-semibold" style={{ fontFamily: 'Outfit' }}>Monetization Opportunities</h3>
        </div>
        <MonetizationSection token={token} />
      </div>
    </div>
  );
}
